import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAuth } from './AuthContext';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { acceptRecipientInvite } from '../../services/api';
import { storePartnerCode } from '../../utils/partnerTracking';
import { getDefaultRouteForUser } from '../../utils/onboardingStatus';
import { track } from '../../services/measurement';
import '../auth/AuthForms.scss';

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [error, setError] = useState('');
  const [errorAction, setErrorAction] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showGoogleLinkPassword, setShowGoogleLinkPassword] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState('');
  const [googleLinkPassword, setGoogleLinkPassword] = useState('');
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [googleButtonWidth, setGoogleButtonWidth] = useState('360');
  const googleWrapperRef = useRef(null);
  const { login, loginWithGoogle, linkGoogleAccount, currentUser, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get('invite_token');
  const partnerCode = queryParams.get('partner');
  const nextParam = queryParams.get('next');
  const returnTo = location.state?.returnTo || location.state?.from?.pathname;
  const registerParams = new URLSearchParams();
  if (inviteToken) registerParams.set('invite_token', inviteToken);
  if (partnerCode) registerParams.set('partner', partnerCode);
  if (nextParam) registerParams.set('next', nextParam);
  const registerQuery = registerParams.toString();
  const registerPath = registerQuery ? `/register?${registerQuery}` : '/register';

  useEffect(() => {
    if (partnerCode) {
      storePartnerCode(partnerCode);
    }
  }, [partnerCode]);

  useEffect(() => {
    if (location.state?.googleLinkRequired) {
      setError(t('login.google_link.restart_instruction'));
      window.history.replaceState({}, document.title);
    }
  }, [location.state, t]);

  const resolveRedirectPath = useCallback((user) => {
    return getDefaultRouteForUser(user, {
      nextParam,
      returnTo,
      inviteToken,
      partnerCode,
      preferActiveApplications: true,
    });
  }, [inviteToken, nextParam, partnerCode, returnTo]);

  const resolveInviteRedirectPath = useCallback((offerId) => {
    const targetPath = `/creator/invited/${offerId}`;
    sessionStorage.removeItem('foth_invite_return_to');
    return targetPath;
  }, []);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required(t('common:required')),
    password: Yup.string().required(t('common:required')),
  });

  const resumePendingRegistration = useCallback((data) => {
    if (!data?.user_id || !data?.email) return false;

    sessionStorage.setItem('foth_pending_registration', JSON.stringify({
      userId: data.user_id,
      email: data.email
    }));

    if (data.needs_role || data.user_type === 'none') {
      navigate(registerPath, { replace: true });
      return true;
    }

    sessionStorage.setItem('foth_pending_verification', JSON.stringify({
      userId: data.user_id,
      email: data.email,
      role: data.role || data.user_type,
      inviteToken
    }));
    navigate('/verify-account', {
      replace: true,
      state: {
        userId: data.user_id,
        email: data.email,
        role: data.role || data.user_type,
        inviteToken,
      }
    });
    return true;
  }, [inviteToken, navigate, registerPath]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const updateGoogleButtonWidth = () => {
      if (googleWrapperRef.current) {
        const measured = googleWrapperRef.current.offsetWidth;
        if (measured > 150) {
          setGoogleButtonWidth(String(Math.min(400, Math.floor(measured))));
          return;
        }
      }
      const viewportWidth = window.innerWidth || 400;
      const deduction = viewportWidth <= 600 ? 72 : 80;
      const safeWidth = Math.min(400, Math.max(260, viewportWidth - deduction));
      setGoogleButtonWidth(String(Math.floor(safeWidth)));
    };

    updateGoogleButtonWidth();
    window.addEventListener('resize', updateGoogleButtonWidth);
    const tick = setTimeout(updateGoogleButtonWidth, 120);
    return () => {
      window.removeEventListener('resize', updateGoogleButtonWidth);
      clearTimeout(tick);
    };
  }, []);

  useEffect(() => {
    if (loginSuccess && currentUser && !loading) {
      navigate(resolveRedirectPath(currentUser), { replace: true });
    }
  }, [currentUser, loading, navigate, loginSuccess, resolveRedirectPath]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      setErrorAction(null);
      const result = await login(values.username, values.password);
      track('login', { method: 'password', user_role: result.user?.user_type });
      if (inviteToken && result.user?.user_type === 'creator') {
        const accepted = await acceptRecipientInvite(inviteToken);
        sessionStorage.removeItem('foth_invite_token');
        await refreshUser();
        navigate(resolveInviteRedirectPath(accepted.data.offer_id), { replace: true });
        return;
      }
      setLoginSuccess(true);
      navigate(resolveRedirectPath(result.user), { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const data = error.response?.data || {};
      if (data.code === 'account_pending_verification' && resumePendingRegistration(data)) {
        return;
      }
      const message = data.error || (
        error.response?.status === 401 ? t('errors.generic_error') : t('errors.connection_error')
      );
      setError(message);
      setErrorAction(data.needs_registration || data.code === 'account_not_found'
        ? { to: registerPath, label: t('login.errors.register_link') }
        : null);
      toast.error(message);
      setLoginSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setError('');
      setErrorAction(null);
      setIsLoading(true);
      const result = await loginWithGoogle(credentialResponse.credential);
      track('login', { method: 'google', user_role: result.user?.user_type });
      if (inviteToken && result.user?.user_type === 'creator') {
        const accepted = await acceptRecipientInvite(inviteToken);
        sessionStorage.removeItem('foth_invite_token');
        await refreshUser();
        navigate(resolveInviteRedirectPath(accepted.data.offer_id), { replace: true });
        return;
      }
      setLoginSuccess(true);
      navigate(resolveRedirectPath(result.user), { replace: true });
    } catch (error) {
      const data = error.response?.data || {};
      if (data.code === 'google_link_required') {
        setPendingGoogleCredential(credentialResponse.credential);
        setGoogleLinkPassword('');
        setError('');
        setErrorAction(null);
        setLoginSuccess(false);
        setIsLoading(false);
        return;
      }
      const message = data.error || t('errors.google_error');
      setError(message);
      setErrorAction(data.needs_registration || data.code === 'account_not_found'
        ? { to: registerPath, label: t('login.errors.register_link') }
        : null);
      toast.error(message);
      setLoginSuccess(false);
      setIsLoading(false);
    }
  };

  const handleGoogleLink = async (event) => {
    event.preventDefault();
    if (!googleLinkPassword) {
      setError(t('login.google_link.password_required'));
      return;
    }

    try {
      setIsLinkingGoogle(true);
      setError('');
      const result = await linkGoogleAccount(pendingGoogleCredential, googleLinkPassword);
      track('login', { method: 'google_link', user_role: result.user?.user_type });
      setPendingGoogleCredential('');
      setGoogleLinkPassword('');
      if (inviteToken && result.user?.user_type === 'creator') {
        const accepted = await acceptRecipientInvite(inviteToken);
        sessionStorage.removeItem('foth_invite_token');
        await refreshUser();
        navigate(resolveInviteRedirectPath(accepted.data.offer_id), { replace: true });
        return;
      }
      setLoginSuccess(true);
      navigate(resolveRedirectPath(result.user), { replace: true });
    } catch (linkError) {
      const data = linkError.response?.data || {};
      const message = data.code === 'invalid_credentials'
        ? t('login.google_link.invalid_password')
        : (data.error || t('errors.google_error'));
      setError(message);
      toast.error(message);
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  const cancelGoogleLink = () => {
    setPendingGoogleCredential('');
    setGoogleLinkPassword('');
    setError('');
  };

  useEffect(() => {
    if (!loading && currentUser && !loginSuccess) {
      navigate(resolveRedirectPath(currentUser), { replace: true });
    }
  }, [loading, currentUser, loginSuccess, navigate, resolveRedirectPath]);

  if (isLoading || loading) {
    return (
      <div className="auth-loading">
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page editorial-split">
      {/* Editorial Left Side */}
      <div className="auth-editorial-side">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="editorial-bg-overlay"
            style={{ backgroundImage: "url('/luxury_auth_bg_2.png')" }}
          />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
            <div className="editorial-text">
               <motion.h1
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.8 }}
               >
                 {t('login.editorial.title')}
               </motion.h1>
               <motion.p
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.3 }}
               >
                 {t('login.editorial.text')}
               </motion.p>
            </div>
          </div>
      </div>

      {/* Form Right Side */}
      <div className="auth-form-side">
        <div className="auth-container">
          {/* Persistent Mobile Header */}
          <div className="auth-mobile-header">
            <Link to="/" className="mobile-back-btn" aria-label={t('common:back', 'Volver')}>
              <ArrowLeft size={18} />
            </Link>
            <div className="mobile-brand-box">
              <img src="/foth-luxury-icon.png" alt="FOTH" className="mobile-logo" />
            </div>
            <div className="spacer" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="auth-card-modern"
          >
            <div className="auth-header">
              <h1 className="auth-title">{t('login.title')}</h1>
              <p className="auth-subtitle">{t('login.subtitle')}</p>
            </div>

            <div className="auth-body">
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="alert-premium error"
                  >
                    <AlertCircle size={18} /> {error}
                    {errorAction && (
                      <>
                        {' '}
                        <Link to={errorAction.to}>{errorAction.label}</Link>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="google-auth-wrapper" ref={googleWrapperRef}>
                <div className="google-auth-provider">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError(t('errors.google_error'))}
                    theme="filled_black"
                    shape="pill"
                    text="continue_with"
                    width={googleButtonWidth}
                  />
                </div>
              </div>

              <AnimatePresence>
                {pendingGoogleCredential && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="google-link-confirmation"
                    onSubmit={handleGoogleLink}
                  >
                    <div className="google-link-copy">
                      <h2>{t('login.google_link.title')}</h2>
                      <p>{t('login.google_link.description')}</p>
                    </div>
                    <label className="form-label" htmlFor="google-link-password">
                      {t('login.google_link.password_label')}
                    </label>
                    <div className="input-premium-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        id="google-link-password"
                        type={showGoogleLinkPassword ? 'text' : 'password'}
                        className="form-control-premium"
                        value={googleLinkPassword}
                        onChange={(event) => setGoogleLinkPassword(event.target.value)}
                        placeholder={t('login.google_link.password_placeholder')}
                        autoComplete="current-password"
                        autoFocus
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowGoogleLinkPassword(!showGoogleLinkPassword)}
                        aria-label={showGoogleLinkPassword ? t('login.google_link.hide_password') : t('login.google_link.show_password')}
                      >
                        {showGoogleLinkPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <div className="google-link-actions">
                      <button type="button" className="google-link-cancel" onClick={cancelGoogleLink} disabled={isLinkingGoogle}>
                        {t('login.google_link.cancel')}
                      </button>
                      <button type="submit" className="google-link-submit" disabled={isLinkingGoogle}>
                        {isLinkingGoogle ? t('login.google_link.linking') : t('login.google_link.submit')}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className="auth-divider">
                <span>{t('login.google')}</span>
              </div>

              <Formik
                initialValues={{ username: '', password: '' }}
                validationSchema={LoginSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="auth-form">
                    <div className="form-group-full">
                      <label htmlFor="login-username" className="form-label">{t('login.form.username', 'Usuario o email')}</label>
                      <div className="input-premium-wrapper">
                        <Mail size={18} className="input-icon" />
                        <Field
                          type="text"
                          name="username"
                          id="login-username"
                          className="form-control-premium"
                          placeholder={t('login.form.placeholder.username', 'tu@email.com')}
                          autoComplete="username"
                        />
                      </div>
                      <ErrorMessage name="username" component="div" className="error-message" />
                    </div>

                    <div className="form-group-full">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label htmlFor="login-password" className="form-label">{t('login.form.password', 'Contraseña')}</label>
                        <Link to="/forgot-password" style={{ fontSize: '12px', color: '#b89025', fontWeight: 500, textDecoration: 'none' }}>
                          {t('login.forgot', '¿Olvidaste tu contraseña?')}
                        </Link>
                      </div>
                      <div className="input-premium-wrapper">
                        <Lock size={18} className="input-icon" />
                        <Field
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          id="login-password"
                          className="form-control-premium"
                          placeholder={t('login.form.placeholder.password', '••••••••')}
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <ErrorMessage name="password" component="div" className="error-message" />
                    </div>

                    <motion.button
                      type="submit"
                      className="auth-submit-btn-premium"
                      disabled={isSubmitting}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmitting ? (
                        <span className="btn-spinner" />
                      ) : (
                        <>{t('login.submit')} <ArrowRight size={18} /></>
                      )}
                    </motion.button>
                  </Form>
                )}
              </Formik>

              <div className="auth-footer">
                <p>
                  {t('login.footer.no_account')}{' '}
                  <Link to={registerPath}>{t('login.footer.register_link')}</Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
